/**
 * Date utilities — timezone-safe formatting.
 *
 * Problem: `new Date("2026-04-25")` is parsed as UTC midnight.
 * In timezones west of UTC (e.g. America/Bogota UTC-5), this shows as the
 * previous day when calling `.toLocaleDateString()`.
 *
 * Solution: parse YYYY-MM-DD strings as local dates, not UTC.
 */

/**
 * Parse a date string safely (treats date-only strings as local).
 * Accepts: "2026-04-25", "2026-04-25T00:00:00", "2026-04-25T10:30:00.000Z", Date instance.
 */
export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;

  // Take only the date portion (handles "2026-04-25T..." or just "2026-04-25")
  const datePart = value.split('T')[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    // Construct as LOCAL date (month is 0-indexed)
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }

  // Fallback: trust the original string (e.g. full ISO with timezone)
  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Format a date for Spanish locale: DD/MM/YYYY.
 * Returns '-' for null/undefined/invalid values.
 */
export function formatDate(value, locale = 'es-ES') {
  const d = parseLocalDate(value);
  if (!d) return '-';
  return d.toLocaleDateString(locale);
}

/**
 * Format a datetime for Spanish locale.
 */
export function formatDateTime(value, locale = 'es-ES') {
  const d = parseLocalDate(value);
  if (!d) return '-';
  return d.toLocaleString(locale);
}

/**
 * Parse an ISO datetime string that was stored as UTC on the server but
 * may come back WITHOUT a timezone marker (which JS would otherwise parse
 * as local time). This function forces UTC interpretation.
 *
 * Accepts:
 *   "2026-08-08T15:08:46"          → interpreted as UTC
 *   "2026-08-08T15:08:46.123"      → interpreted as UTC
 *   "2026-08-08T15:08:46.123+00:00"→ respects the timezone marker
 *   "2026-08-08T15:08:46.123Z"     → respects Z
 *   Date instance                  → returned as-is
 */
export function parseServerUTC(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;
  let iso = value.trim();
  // Only add Z if there's a time component AND no timezone marker
  if (iso.includes('T')) {
    const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
    if (!hasTz) iso += 'Z';
  }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format the time component (HH:MM) of a UTC timestamp in the user's LOCAL timezone.
 * Perfect for showing "hora" en tablas de movimientos, historial, logs.
 * Example: server stores "2026-08-08T15:08:46" (UTC), user in UTC-4 sees "11:08".
 */
export function formatLocalTime(value, locale = 'es-ES') {
  const d = parseServerUTC(value);
  if (!d) return '—';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * Format a full datetime (date + time) using the user's local timezone.
 */
export function formatLocalDateTime(value, locale = 'es-ES') {
  const d = parseServerUTC(value);
  if (!d) return '—';
  return d.toLocaleString(locale, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

/**
 * Anchor a Date to the Monday of its week (ISO standard - matches backend).
 * Returns a new Date at 00:00 local time.
 */
function anchorToMonday(d) {
  const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const offset = day === 0 ? 6 : day - 1; // days to subtract to reach Monday
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
  m.setHours(0, 0, 0, 0);
  return m;
}

/**
 * Compute the age-in-weeks of a flock at a given reference date,
 * anchored to Monday (matches backend `calcular_semanas_edad`).
 *
 * @param {string|Date} fechaRegistro  Date when the production record was taken.
 * @param {string|Date} fechaRefLote   Lote's `fecha_referencia_edad` (or `fecha_ingreso`).
 * @returns {number|null} Number of full weeks (>=0), or null if invalid input.
 */
export function calcularSemanaEnFecha(fechaRegistro, fechaRefLote) {
  const fReg = parseLocalDate(fechaRegistro);
  const fRef = parseLocalDate(fechaRefLote);
  if (!fReg || !fRef) return null;
  const lunesReg = anchorToMonday(fReg);
  const lunesRef = anchorToMonday(fRef);
  const diffMs = lunesReg.getTime() - lunesRef.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, Math.floor(diffDays / 7));
}
